import React, { useState, useEffect } from 'react';
import { Box, Button, List, ListItem, ListItemText, ListItemIcon, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Chip, TextField, MenuItem } from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import { supabase } from '../../lib/supabase';
import { fetchWorkers } from '../../services/workerService';

const NotificationsPanel: React.FC<{ farmId: string }> = ({ farmId }) => {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [type, setType] = useState('Announcement');
  const [workers, setWorkers] = useState<any[]>([]);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [editNotification, setEditNotification] = useState<any | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean, notification: any | null }>({ open: false, notification: null });

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchWorkers(farmId)
      .then(async (ws) => {
        setWorkers(ws);
        const workerIds = ws.map((w: any) => w.id);
        // Fetch notifications for all workers in this farm
        const { data, error } = await supabase
          .from('notifications')
          .select('*')
          .in('user_id', workerIds)
          .order('created_at', { ascending: false });
        if (error) throw error;
        setNotifications(data || []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [farmId, success]);

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      // Send to all workers
      const inserts = workers.map(w => ({
        user_id: w.id,
        type,
        title: type,
        message,
        channel: 'in_app',
        created_at: new Date().toISOString(),
      }));
      const { error } = await supabase.from('notifications').insert(inserts);
      if (error) throw error;
      setSuccess('Announcement sent!');
      setOpen(false);
      setMessage('');
      setType('Announcement');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleEditNotification = async () => {
    if (!editNotification) return;
    setSending(true);
    setError(null);
    try {
      const { error } = await supabase
        .from('notifications')
        .update({
          type: editNotification.type,
          title: editNotification.type,
          message: editNotification.message,
        })
        .eq('id', editNotification.id);
      if (error) throw error;
      setSuccess('Notification updated!');
      setEditNotification(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleDeleteNotification = async () => {
    if (!deleteConfirm.notification) return;
    setSending(true);
    setError(null);
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', deleteConfirm.notification.id);
      if (error) throw error;
      setSuccess('Notification deleted!');
      setDeleteConfirm({ open: false, notification: null });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Notifications</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>Send Announcement</Button>
      </Box>
      {loading ? <Typography>Loading...</Typography> : error ? <Typography color="error">{error}</Typography> : null}
      <List>
        {notifications.map(n => (
          <ListItem key={n.id} secondaryAction={
            <>
              <IconButton color="secondary" onClick={() => setEditNotification(n)}><EditIcon /></IconButton>
              <IconButton color="error" onClick={() => setDeleteConfirm({ open: true, notification: n })}><DeleteIcon /></IconButton>
            </>
          }>
            <ListItemIcon><CampaignIcon color={n.type === 'Announcement' ? 'primary' : 'info'} /></ListItemIcon>
            <ListItemText
              primary={<><Chip label={n.type} size="small" sx={{ mr: 1 }} />{n.message}</>}
              secondary={new Date(n.created_at).toLocaleString()}
            />
          </ListItem>
        ))}
      </List>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Send Announcement</DialogTitle>
        <DialogContent>
          <TextField
            select
            label="Type"
            value={type}
            onChange={e => setType(e.target.value)}
            fullWidth
            margin="normal"
          >
            <MenuItem value="Announcement">Announcement</MenuItem>
            <MenuItem value="Reminder">Reminder</MenuItem>
          </TextField>
          <TextField
            label="Message"
            value={message}
            onChange={e => setMessage(e.target.value)}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSend} disabled={sending || !message}>{sending ? 'Sending...' : 'Send'}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!editNotification} onClose={() => setEditNotification(null)}>
        <DialogTitle>Edit Notification</DialogTitle>
        <DialogContent>
          {editNotification && (
            <>
              <TextField
                select
                label="Type"
                value={editNotification.type}
                onChange={e => setEditNotification({ ...editNotification, type: e.target.value })}
                fullWidth
                margin="normal"
              >
                <MenuItem value="Announcement">Announcement</MenuItem>
                <MenuItem value="Reminder">Reminder</MenuItem>
              </TextField>
              <TextField
                label="Message"
                value={editNotification.message}
                onChange={e => setEditNotification({ ...editNotification, message: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={3}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNotification(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditNotification} disabled={sending || !editNotification?.message}>{sending ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, notification: null })}>
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this notification?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, notification: null })}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteNotification} disabled={sending}>{sending ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
      {success && <Typography color="success.main" sx={{ mt: 2 }}>{success}</Typography>}
    </Box>
  );
};

export default NotificationsPanel; 